import { __ } from '@wordpress/i18n';

/**
 * EDD (Easy Digital Downloads) Source Utilities
 *
 * Shared helper functions for handling multi-download EDD sources.
 * Used by SelectSourcesSection.js, FeedSources.js, and other components.
 */

/**
 * Parse source info for multi-download EDD sources
 *
 * @param {Object} source - The source object
 * @return {Object|null} Parsed info object or null if not a multi-download source
 */
export const parseSourceInfo = ( source ) => {
    if ( !source || source.provider !== 'edd' ) {
        return null;
    }

    let info = source.info;

    if ( typeof info === 'string' ) {
        try {
            info = JSON.parse( info );
        } catch ( e ) {
            // Invalid JSON, return null
            return null;
        }
    }

    // Return info for any EDD source (not just multi_download type)
    // This ensures backwards compatibility with older sources
    return info || null;
};

/**
 * Get badge text for multi-download EDD sources
 *
 * @param {Object} source - The source object
 * @return {string|null} Badge text like "5 Downloads" or null
 */
export const getSourceBadge = ( source ) => {
    const info = parseSourceInfo( source );

    if ( !info || !info.download_count ) {
        return null;
    }

    const count = parseInt( info.download_count, 10 );
    return count + ' ' + ( count === 1 ? __( 'Download', 'sb-customizer' ) : __( 'Downloads', 'sb-customizer' ) );
};

/**
 * Get downloads info for multi-download EDD sources
 * Returns only individually selected downloads (direct_downloads)
 *
 * @param {Object} source - The source object
 * @return {Array|null} Array of download objects or null
 */
export const getSourceDownloads = ( source ) => {
    const info = parseSourceInfo( source );
    return info?.direct_downloads || null;
};

/**
 * Get categories info for multi-download EDD sources
 *
 * @param {Object} source - The source object
 * @return {Array|null} Array of category objects or null
 */
export const getSourceCategories = ( source ) => {
    const info = parseSourceInfo( source );
    return info?.categories || null;
};

/**
 * Get tags info for multi-download EDD sources
 *
 * @param {Object} source - The source object
 * @return {Array|null} Array of tag objects or null
 */
export const getSourceTags = ( source ) => {
    const info = parseSourceInfo( source );
    return info?.tags || null;
};

/**
 * Check if a source is a multi-download EDD source
 *
 * Multi-download sources have:
 * - account_id starting with 'edd_multi_'
 * - OR info containing downloads/category_ids/tag_ids arrays
 *
 * Single-download sources have:
 * - account_id as the numeric download ID
 * - info may be empty or contain basic metadata
 *
 * @param {Object} source - The source object
 * @return {boolean} True if multi-download EDD source
 */
export const isMultiDownloadSource = ( source ) => {
    if ( !source || source.provider !== 'edd' ) {
        return false;
    }

    // Check account_id prefix (primary method)
    if ( source.account_id && String( source.account_id ).startsWith( 'edd_multi_' ) ) {
        return true;
    }

    // Fallback: check info structure for multi-download indicators
    const info = parseSourceInfo( source );
    if ( info ) {
        // Multi-download sources have downloads, category_ids, or tag_ids arrays
        const hasDownloads = Array.isArray( info.downloads ) && info.downloads.length > 0;
        const hasCategories = Array.isArray( info.category_ids ) && info.category_ids.length > 0;
        const hasTags = Array.isArray( info.tag_ids ) && info.tag_ids.length > 0;

        return hasDownloads || hasCategories || hasTags;
    }

    return false;
};

const EddSourceUtils = {
    parseSourceInfo,
    getSourceBadge,
    getSourceDownloads,
    getSourceCategories,
    getSourceTags,
    isMultiDownloadSource
};

export default EddSourceUtils;
